import { call, put, putResolve, select } from "redux-saga/effects";
import type { FinalizeResponse } from "@capone/packages-api";
import {
  FinalizePackageResponse,
  PackagedRoom,
  Platform,
  SafePackageDetails,
} from "redmond";
import { actions } from "../actions";
import { IFinalizePackage } from "../actions/actions";
import Logger from "../../../utils/logger";
import { selectedPackageByRateId } from "../../hotel-shop/reducer";
import { IStoreState } from "../../../reducers/types";
import { getSelectedReturnFlightPackage } from "../../flight-shop/reducer";
import { getPackageSearchKey } from "../../package-identity/reducer/selectors";
import { actions as packageIdentityActions } from "../../package-identity/actions";
import { finalizeFlightLodging } from "../../../api/v0/itinerary-review/finalizeFlightLodging";
import { finalizePackageV2 } from "../../../api/v2/itinerary-review/finalizePackage";
import { convertFinalizeResponseToFinalizePackageResponse } from "../../../api/v2/itinerary-review/convertFinalizeResponse";
import { pollPackageVersion } from "../utils/pollPackageVersion";
import { setDateRange } from "../../hotel-shop/actions/actions";

export default function* finalizePackageSaga(action: IFinalizePackage) {
  try {
    const state: IStoreState = yield select();
    const selectedPackage: PackagedRoom | undefined =
      yield selectedPackageByRateId(state);
    const selectedFlightPackage: SafePackageDetails | undefined =
      yield getSelectedReturnFlightPackage(state);
    const lodgingQuoteRequest = selectedPackage?.opaqueQuoteRequest;
    const flightPackageToken = selectedFlightPackage?.opaquePackageToken;

    if (!lodgingQuoteRequest || !flightPackageToken) {
      throw new Error("Hotel and flight tokens must be present.");
    }

    const searchKey = getPackageSearchKey(state);
    if (searchKey != null && action.versionPollTarget != null) {
      yield call(pollPackageVersion, {
        searchKey,
        targetVersion: action.versionPollTarget,
      });
    }

    let response: FinalizePackageResponse;
    if (action.useV2) {
      if (searchKey == null) {
        throw new Error(
          "finalizePackageSaga: searchKey must be present to call v2 finalize."
        );
      }
      const v2Response: FinalizeResponse = yield call(finalizePackageV2, {
        searchKey: { value: searchKey },
      });
      // Route finalize's packageDisplayData through the same Redux path the poll uses — see
      // finalizePackageService's matching comment for why (it can return a requoted room).
      yield put(
        packageIdentityActions.setPackageDisplayData(
          searchKey,
          v2Response.packageDisplayData
        )
      );
      response = convertFinalizeResponseToFinalizePackageResponse(v2Response);
    } else {
      response = yield call(finalizeFlightLodging, {
        opaqueQuoteRequest: lodgingQuoteRequest,
        opaquePackageToken: flightPackageToken,
        platform: Platform.Desktop,
      });
    }

    if (
      !!response?.lodgingStayDates?.from &&
      !!response?.lodgingStayDates?.until
    ) {
      yield putResolve(setDateRange(response.lodgingStayDates));
    }

    yield putResolve(actions.setFinalizePackageResponse(response));
  } catch (e) {
    Logger.debug(e);
    yield put(actions.setFinalizePackageResponseCallStateFailed(e));
  }
}

